import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Building2, Trash2, Search, Pencil } from "lucide-react";
import { canDeleteRecord } from "@/lib/perms";
import { toast } from "sonner";

const TIER_COLORS = {
  level_1: "bg-sky-50 text-sky-700 border-sky-200",
  level_2: "bg-amber-50 text-amber-700 border-amber-200",
  level_3: "bg-violet-50 text-violet-700 border-violet-200",
};

const TIERS = ["level_1", "level_2", "level_3"];

const emptyForm = { name: "", company: "", email: "", phone: "", industry: "", tier: "level_1", notes: "" };

const Clients = () => {
  const { t } = useLang();
  const { can, user } = useAuth();
  const canWrite = can("clients:write");
  const [clients, setClients] = useState([]);
  const [tierFilter, setTierFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const res = await api.get("/clients");
    setClients(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...emptyForm, ...c }); setOpen(true); };

  const submit = async () => {
    if (!form.name || !form.company) { toast.error("Nama & Perusahaan wajib"); return; }
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, form);
        toast.success(t("save"));
      } else {
        await api.post("/clients", form);
        toast.success(t("save"));
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus klien?")) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success(t("delete"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const filtered = clients.filter((c) =>
    (c.name + " " + c.company + " " + (c.industry || "")).toLowerCase().includes(q.toLowerCase())
    && (tierFilter === "all" || c.tier === tierFilter)
  );

  return (
    <div className="space-y-6" data-testid="client-management-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("clients")}</h2>
          <p className="text-sm text-slate-500 mt-1">{filtered.length} / {clients.length} {t("clients").toLowerCase()}</p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-slate-100 rounded-lg p-1" data-testid="tier-filter-group">
            {["all", ...TIERS].map((tv) => (
              <button
                key={tv}
                data-testid={`tier-filter-${tv}`}
                onClick={() => setTierFilter(tv)}
                className={`px-3 py-1.5 text-sm rounded-md font-medium transition ${tierFilter === tv ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
              >{tv === "all" ? t("all") : t(tv)}</button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              data-testid="search-input"
              placeholder={t("search")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 w-64 bg-white border-slate-300"
            />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            {canWrite && (
              <DialogTrigger asChild>
                <Button onClick={openNew} data-testid="add-client-button" className="bg-orange-500 hover:bg-orange-600 gap-2">
                  <Plus className="w-4 h-4" /> {t("add_client")}
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editing ? t("edit") : t("new_client")}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">{t("name")}</Label>
                  <Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">{t("company")}</Label>
                  <Input data-testid="client-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">{t("email")}</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">{t("phone")}</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">{t("industry")}</Label>
                  <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">{t("tier")}</Label>
                  <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                    <SelectTrigger data-testid="client-tier-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIERS.map((tv) => <SelectItem key={tv} value={tv}>{t(tv)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">{t("notes")}</Label>
                  <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
                <Button onClick={submit} data-testid="save-client-button" className="bg-orange-500 hover:bg-orange-600">{t("save")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-dashed">
          <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">{t("empty_clients")}</p>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-3">{t("name")}</th>
                  <th className="px-4 py-3">{t("company")}</th>
                  <th className="px-4 py-3">{t("industry")}</th>
                  <th className="px-4 py-3">{t("tier")}</th>
                  <th className="px-4 py-3">{t("contact")}</th>
                  <th className="px-4 py-3">{t("created_by")}</th>
                  <th className="px-4 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`client-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                    <td className="px-4 py-3 text-slate-700">{c.company}</td>
                    <td className="px-4 py-3 text-slate-500">{c.industry || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${TIER_COLORS[c.tier] || TIER_COLORS.level_1}`}>
                        {t(c.tier)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      <div>{c.email}</div>
                      <div>{c.phone}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{c.created_by_name || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      {canWrite && (
                        <button onClick={() => openEdit(c)} className="p-1.5 text-slate-500 hover:text-slate-900" data-testid={`edit-client-${c.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {canDeleteRecord(user, c, "created_by") && (
                        <button onClick={() => del(c.id)} className="p-1.5 text-slate-500 hover:text-rose-600 ml-1" data-testid={`delete-client-${c.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Clients;
